import { useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { DatePicker } from '@/components/ui/date-picker.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  logQuoteReceived,
  patchQuote,
  presentQuote,
  getQuote,
  getQuotePrefill,
  listCarriers,
} from '@/services/quotes.service.js';
import { createScenarioQuote, patchScenarioQuote } from '@/services/submission.service.js';
import { ApiError } from '@/lib/api.js';
import { cn } from '@/lib/utils.js';
import { ListOptions } from '@/components/shared/ListSelect.jsx';
// The field vocabulary and input primitives moved OUT of this file (unchanged)
// so QuoteWizardSheet asks the same questions with the same controls rather than
// growing a second parseDeductible. QUOTE-WIZARD-SPEC phase 1.
import {
  PART_TYPE_PICKLIST, OPW_CERTS, OPW_RATINGS,
  MEDICAL_QUICK, LIAB_OCC_QUICK, LIAB_PAX_QUICK,
  coveragePartKey, toNumber, fmtMoney, normTail, parseDeductible, deductiblePreview,
  buildBrokerNotes, splitBrokerNotes, parsePilotRequirements, encodePilotRequirements, buildCommonRollup,
} from '@/lib/quoteFields.js';
import { inputClass, MoneyInput, AutoTextarea, Field, Helper } from '@/components/quote/QuoteControls.jsx';
import TermPicker, { termMonthsOf, termSeed, TERM_MAX_MONTHS } from '@/components/TermPicker.jsx';
import { presentSaveNotices } from '@/lib/quoteCompleteness.js';
// The derivation, imported rather than restated. Both quote surfaces must agree
// about what "conditional" means, or the same quote reads two ways.
import { encodeApprovalStatus } from '@/lib/quoteWizardModel.js';

// ─── QuoteEntrySheet — single progressive-disclosure quote surface ───
//
// Replaces the prior two-surface model (LogQuoteReceivedSheet + the
// modal QuoteEntryModal hand-off via "Expand to full") per Vision §V2
// fix #6 (2026-05-20). The two-surface model was conceptual overhead
// — broker fills what they have; the depth they go to is determined
// by what they expand, not by which menu item they picked.
//
// Layout (top → bottom):
//   1. REQUIRED — gates Save. Always visible. (Carrier, Premium,
//      Coverage Type, Effective Date)
//   2. COVERAGE — default-visible primary coverage values (Liability
//      mode + values + medical, per-aircraft Hull, Deductibles, NPO
//      toggle, Broker notes)
//   3. COLLAPSED SECTIONS — default-closed, expand-as-needed:
//      Pilot Warranty (full OPW detail when NPO off; Approved Pilots when NPO on)
//      Premium Breakdown
//      Coverage Parts
//      Subjectivities
//      Source Document
//      Internal flags (client_visible, broker_recommended)
//      Per-aircraft schedule (advanced — per-row use / deductibles / training)
//      Quote reference (UW / quote ref / dates / term / territory)
//
// Mobile (<768px): COVERAGE collapses to keep the sheet from being
// scroll-heavy; broker expands only what they need.
//
// Save modes:
//   • Save                       — persists, sheet closes
//   • Save and Mark Client Visible — client_visible=true (surfaces on tower.html)
//   • Save and Present           — client_visible=true + presentQuote() +
//                                  parent opens ComposeSheet with 'quotes-ready'
//                                  via onSaved({present:true, quotationId, sessionToken})
//
// Edit-existing-quote: when `quotationId` is set, the sheet pre-
// populates via GET /quotes/:id and saves via PATCH. Every
// aircraft_schedule[] / coverage_parts[] row carries its `id` so
// Zoho doesn't duplicate the subform (CLAUDE.md rule).

// The picklists (PART_TYPE_PICKLIST, OPW_*, TERM_MONTHS,
// the quick-pick amounts) and the pure helpers (toNumber,
// fmtMoney, normTail, parseDeductible/deductiblePreview, coveragePartKey, the
// broker-notes and pilot-requirement codecs) moved to @/lib/quoteFields.js,
// unchanged, and are imported above.
//
// Aircraft use and coverage type are picks from the one list for each (utils/answer-lists through GET
// /api/tracon/answer-lists, shared/ListSelect ListOptions; David 2026-11-04), NOT a local hardcode. The old
// COVERAGE_TYPES const mislabeled use as coverage type (Task K) and its 6-value
// list had drifted (missing Flying Club et al). → quotes.aircraft_use →
// Quotation.Aircraft_Usage.

export default function QuoteEntrySheet({
  dealId,
  quotationId, // when set, pre-populates for edit
  seedQuote,   // optional: the already-loaded list-row quote. Seeds headline
               // fields synchronously on open so edit mode renders instantly
               // instead of waiting on the getQuote round-trip (the ~1s
               // "eyeball" delay). The authoritative getQuote fetch still runs
               // and overwrites — seed is perceived-latency only.
  open,
  onOpenChange,
  onSaved, // ({present, quotationId, sessionToken}) — parent handles
           // Save+Present by opening ComposeSheet at its own level.
  scenarioScope, // 4b-2 matrix-cell mode (SubmissionBuilderPanel). When set:
                 //   { submissionId, scenarioId,
                 //     quoteId,          — Supabase quotes-row id (edit; null = new)
                 //     crmQuotationId,   — CRM Quotation id (edit prefill via getQuote)
                 //     carrier: {slug, name},   — locks the carrier field to the column
                 //     scenario: {scenario_label, coverage, aircraft_hull},
                 //     includedTails,    — UPPERCASE tails on the submission roster
                 //     ratedPilots }     — [{contact_id, name}] rated roster the
                 //                          pilot×tail APR matrix attaches to
                 // Defaults inherit the scenario; save routes to the 4a
                 // scenario-quote POST/PATCH instead of /quotes/create.
}) {
  // Mobile detection — used to default more sections closed on small
  // viewports. Re-runs on resize (mostly to handle device rotation).
  const isMobile = useMobileViewport();

  // Default-open state per spec: HEADLINE block open on desktop,
  // collapsed on mobile. Detail sections collapsed everywhere.
  const [openSections, setOpenSections] = useState({
    headline: !isMobile, // Liability/Hull/Ded/NPO/Notes
    identity: false,
    warranty: true, // every policy has an OPW unless NPO; NPO toggle lives here — don't bury it
    pilotReqs: true, // the merged "Named Pilots" section (decision + requirement) — default open
    breakdown: false,
    parts: false,
    subjectivities: false,
    source: false,
    internal: false,
    schedule: false, // per-aircraft advanced row editor
  });
  function toggle(k) {
    setOpenSections((s) => ({ ...s, [k]: !s[k] }));
  }

  // Reset open-state when the viewport boundary crosses — broker
  // resizing a desktop window down past 768px shouldn't lose their
  // expanded sections, but the initial open on a fresh sheet should
  // reflect the current viewport.
  useEffect(() => {
    if (!open) return;
    setOpenSections((s) => ({ ...s, headline: !isMobile }));
  }, [open, isMobile]);

  // ── Data state ───────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [savedQuoteId, setSavedQuoteId] = useState(quotationId || null);

  // REQUIRED
  const [carrier, setCarrier] = useState('');
  const [premium, setPremium] = useState('');
  const [aircraftUse, setAircraftUse] = useState('Pleasure & Business');
  // Policy-level hull-coverage scope (Full Flight / GNIM / Liability Only). For
  // a single-aircraft policy this IS the aircraft term; with 2+ aircraft it is
  // the default and each schedule row can override it. Blank until stated.
  const [coverageType, setCoverageType] = useState('');
  // B14: null = untouched, so nothing is sent.
  const [irOpwOnly, setIrOpwOnly] = useState(null);
  const [proposedEffective, setProposedEffective] = useState('');
  const [quoteExpiresAt, setQuoteExpiresAt] = useState('');

  // HEADLINE
  const [liabilityMode, setLiabilityMode] = useState('split');
  const [liabPerOcc, setLiabPerOcc] = useState('');
  const [liabPerPax, setLiabPerPax] = useState('');
  const [inMotionDed, setInMotionDed] = useState('');
  const [notMotionDed, setNotMotionDed] = useState('');
  const [npo, setNpo] = useState(false);
  const [baseNotes, setBaseNotes] = useState('');
  // Client-facing enhancements prose — shown on the tower quote card and
  // copied to the Deal at bind (unlike baseNotes, which is broker-internal).
  const [coverageEnhancements, setCoverageEnhancements] = useState('');
  // Per-aircraft hull values, keyed by tail_number_id.
  const [hullByTail, setHullByTail] = useState({});

  // IDENTITY (collapsed by default)
  const [underwriter, setUnderwriter] = useState('');
  const [carrierRef, setCarrierRef] = useState('');
  const [quoteDate, setQuoteDate] = useState('');
  const [termMonths, setTermMonths] = useState('12');

  // PILOT WARRANTY detail
  const [opwCert, setOpwCert] = useState('');
  const [opwRatings, setOpwRatings] = useState([]);
  const [opwTotalHours, setOpwTotalHours] = useState('');
  const [opwMakeModelHours, setOpwMakeModelHours] = useState('');
  const [opwTypeHours, setOpwTypeHours] = useState('');
  // Requirements that apply to EVERY pilot, each scoped to a tail or to the
  // whole policy. Replaces the single "applies to all" string, which had no
  // aircraft on it and so claimed a Malibu-only clause covered the Warrior too.
  const [commonReqs, setCommonReqs] = useState([]);
  // Per-pilot APR: contact_id → free-text. Rendered as one input per pilot
  // on the deal, in addition to `pilotRequirements` (the "applies to all"
  // boilerplate). On save the per-pilot blocks are concatenated into the
  // single Quotation.Pilot_Requirements text using `[Pilot Name]` markers
  // so the bind step can apply each pilot's text to their own Approved_Pilots
  // row instead of duplicating one block to everyone (2026-05-22 bug:
  // Nathan Miller's training requirement applied to all 4 pilots).
  const [pilotApr, setPilotApr] = useState({});
  // Scenario-mode APR matrix: the sparse pilot×tail array of Additional Pilot
  // Requirements the underwriter imposed ("As relates N123EM, John Doe must
  // complete X hrs dual…"). Canonical in quotes.approved_pilot_requirements;
  // each entry { contact_id, name, tail_number, requirement }. Standalone mode
  // keeps the legacy common/per-pilot fields above instead.
  const [aprEntries, setAprEntries] = useState([]);
  // Per-carrier pilot approval (§4): contact_id → { status, condition_text, name }.
  // Separate from the APR matrix above (training req ≠ approve/decline decision).
  const [approvalByPilot, setApprovalByPilot] = useState({});
  const [nameOnlyStates, setNameOnlyStates] = useState([]);
  const [opwFullText, setOpwFullText] = useState('');

  // True while the premium holds the §5.2 pro-rata clone guess — cleared the
  // moment the broker types a real number. UI-only; never persisted.

  // PREMIUM BREAKDOWN
  const [hullPremium, setHullPremium] = useState('');
  const [liabilityPremium, setLiabilityPremium] = useState('');
  const [triaPremium, setTriaPremium] = useState('');
  const [estimatedTaxes, setEstimatedTaxes] = useState('');

  // COVERAGE PARTS subform
  const [coverageParts, setCoverageParts] = useState([]);

  // SUBJECTIVITIES
  const [subjectivities, setSubjectivities] = useState('');

  // Per-aircraft schedule rows (Hull goes in HEADLINE; the full row
  // — aircraft_use / per-row deductibles / training_required /
  // aircraft_premium — lives in the schedule beneath HEADLINE so the
  // broker can refine without expanding a separate detail section).
  // { id?, tail_number_id, tail_display, year, make_model, hull_value,
  //   aircraft_use, deductible_in_motion, deductible_not_in_motion,
  //   training_required, aircraft_premium }
  const [schedule, setSchedule] = useState([]);

  // Medical payments lives in the Liability fieldset (liability
  // sub-coverage); Territory override lives in Identity (coverage
  // geography).
  const [medical, setMedical] = useState('');
  const [territory, setTerritory] = useState('');

  // INTERNAL
  const [clientVisible, setClientVisible] = useState(false);
  const [brokerRecommended, setBrokerRecommended] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // ── Initial load ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !dealId) return;
    // Reset all form + save state on every (re)open. The parents
    // (DealActionsSheets / QuotesPanel) keep this sheet mounted and only
    // toggle `open`, so without an explicit reset `savedQuoteId` — and
    // every field — survives a close. A stale `savedQuoteId` made the 2nd
    // new quote on a deal PATCH-overwrite the 1st instead of POSTing a new
    // Quotation (repro: deal 6108943000020750041, Global then IAT → one
    // record). Resetting to `quotationId || null` keeps edit mode pointed
    // at its record while forcing new-quote mode back to POST. The
    // edit/new branches below repopulate after this baseline.
    //
    // Scenario mode (4b-2 matrix cell): the CRM Quotation id for edit-prefill
    // comes from the cell's quotes row, and savedQuoteId holds the Supabase
    // quotes-row id — the 4a PATCH target — instead of the CRM id.
    //
    // savedQuoteId gates POST-vs-PATCH on save. A cell can carry a quotes row
    // that was never mirrored (crm_quotation_id null — e.g. a create that died
    // before the mirror, like the B-bug-1 buildScheduleRows throw). Routing
    // that to PATCH 409s ("no CRM Quotation to edit"); it must POST instead so
    // the create path runs (findOrCreateQuoteRow reuses the row — no dup — and
    // resolves aircraft + builds the schedule + mirror-creates). So only treat
    // it as an editable target when a CRM Quotation actually exists.
    // The edit-mode GET reads the canonical Supabase store by the DOMAIN quote
    // id (getQuote -> /quotes/:id -> quotingEngine.getQuoteDetail -> quotes.id),
    // NOT the CRM Quotation id. A scenario cell is editable only when a real
    // mirrored Quotation exists (crmQuotationId present, per the POST-vs-PATCH
    // gate above) — gate on that, but fetch + PATCH by the domain quoteId.
    const editQuotationId = scenarioScope ? (scenarioScope.crmQuotationId ? (scenarioScope.quoteId || null) : null) : (quotationId || null);
    setSavedQuoteId(scenarioScope ? (scenarioScope.crmQuotationId ? (scenarioScope.quoteId || null) : null) : (quotationId || null));
    setCarrier(''); setPremium(''); setAircraftUse('Pleasure & Business');
    setCoverageType('');
    setIrOpwOnly(null);
    setProposedEffective('');
    setLiabilityMode('split'); setLiabPerOcc(''); setLiabPerPax('');
    setInMotionDed(''); setNotMotionDed(''); setNpo(false); setBaseNotes('');
    setCoverageEnhancements('');
    setHullByTail({});
    setUnderwriter(''); setCarrierRef(''); setQuoteDate('');
    setTermMonths('12');
    setOpwCert(''); setOpwRatings([]); setOpwTotalHours('');
    setOpwMakeModelHours(''); setOpwTypeHours(''); setCommonReqs([]);
    setPilotApr({}); setAprEntries([]); setOpwFullText('');
    setHullPremium(''); setLiabilityPremium(''); setTriaPremium('');
    setEstimatedTaxes('');
    setCoverageParts([]); setSubjectivities(''); setSchedule([]);
    setMedical(''); setTerritory('');
    setClientVisible(false); setBrokerRecommended(false);

    // Instant headline seed from the list row (edit mode only). Eliminates the
    // perceived "eyeball" delay — the sheet shows carrier/premium/coverage
    // immediately while the authoritative getQuote() fetch below refines the
    // full record (subforms, premium breakdown, etc.). Seed never wins over the
    // fetch; it's purely perceived-latency. Runs AFTER the reset so it can't
    // reintroduce the stale-savedQuoteId bug guarded by tests/quote-entry.test.js.
    if (editQuotationId && seedQuote) {
      const s = seedQuote;
      if (s.carrier) setCarrier(s.carrier);
      if (s.underwriter) setUnderwriter(s.underwriter);
      if (s.aircraft_use || s.aircraft_usage) setAircraftUse(s.aircraft_use || s.aircraft_usage);
      if (s.proposed_effective) setProposedEffective(String(s.proposed_effective).slice(0, 10));
      if (s.quote_expires_at) setQuoteExpiresAt(String(s.quote_expires_at).slice(0, 10));
      if (s.premium != null) setPremium(String(s.premium));
      if (s.liability_per_occurrence != null) setLiabPerOcc(String(s.liability_per_occurrence));
      if (s.liability_per_passenger != null) { setLiabPerPax(String(s.liability_per_passenger)); setLiabilityMode('split'); }
      if (s.liability_smooth != null) setLiabilityMode(s.liability_smooth ? 'smooth' : 'split');
      if (s.coverage_enhancements != null) setCoverageEnhancements(s.coverage_enhancements);
      if (s.medical_per_person != null) setMedical(String(s.medical_per_person));
      if (s.in_motion_deductible) setInMotionDed(String(s.in_motion_deductible));
      if (s.non_motion_deductible) setNotMotionDed(String(s.non_motion_deductible));
      if (s.named_pilots_only != null) setNpo(!!s.named_pilots_only);
      setClientVisible(!!s.client_visible);
      setBrokerRecommended(!!s.broker_recommended);
    }

    // Scenario-scope seeding (4b-2): the cell pre-scopes the carrier; a NEW
    // quote inherits the scenario's coverage as defaults (hull/schedule
    // seeding happens with the prefill fetch below). Broker can override
    // anything before save — overrides ride the same payload fields.
    if (scenarioScope) {
      setCarrier(scenarioScope.carrier?.name || '');
      if (!editQuotationId) {
        const cov = scenarioScope.scenario?.coverage || {};
        if (cov.liability_per_occurrence != null) setLiabPerOcc(String(cov.liability_per_occurrence));
        if (cov.liability_per_passenger != null) { setLiabPerPax(String(cov.liability_per_passenger)); setLiabilityMode('split'); }
        else if (cov.liability_per_occurrence != null) setLiabilityMode('smooth');
        if (cov.medical_payments != null) setMedical(String(cov.medical_payments));
        if (cov.deductible_in_motion != null) setInMotionDed(String(cov.deductible_in_motion));
        if (cov.deductible_not_in_motion != null) setNotMotionDed(String(cov.deductible_not_in_motion));
      }
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [pf, c] = await Promise.all([
          getQuotePrefill(dealId).catch(() => null),
          listCarriers().catch(() => null),
        ]);
        if (cancelled) return;
        setPrefill(pf);
        setCarriers(c?.carriers || []);
        if (!editQuotationId) {
          setApprovalByPilot({}); // fresh quote → no approval decisions yet
          // New-quote defaults — default_effective is computed server-side
          // (pre-staged renewal deal → Policy_Start; legacy expiring deal →
          // Policy_End_Date) and arrives as bare YYYY-MM-DD.
          if (pf?.deal?.default_effective) {
            setProposedEffective(String(pf.deal.default_effective).slice(0, 10));
          }
          // Seed schedule + hull defaults from prefill aircraft. In scenario
          // mode, scope to the submission's included tails and let the
          // scenario's aircraft_hull win over the AoP hull default (4b-2:
          // defaults inherit the scenario; broker can override anything).
          let aircraft = pf?.aircraft || [];
          const scenHullByTail = new Map(
            (scenarioScope?.scenario?.aircraft_hull || [])
              .filter((h) => h && h.tail_number && h.hull_value != null)
              .map((h) => [normTail(h.tail_number), h.hull_value])
          );
          if (scenarioScope?.includedTails?.length) {
            const inc = new Set(scenarioScope.includedTails);
            const scoped = aircraft.filter((a) => inc.has(normTail(a.tail_display)));
            if (scoped.length) aircraft = scoped; // tail-format mismatch → keep all, never an empty schedule
          }
          const seedHull = (a) => {
            const scen = scenHullByTail.get(normTail(a.tail_display));
            const v = scen != null ? scen : a.current_hull_value;
            return v != null ? String(v) : '';
          };
          setSchedule(aircraft.map((a) => ({
            tail_number_id: a.tail_number_id,
            tail_display: a.tail_display,
            year: a.year,
            make_model: a.make_model,
            hull_value: seedHull(a),
            coverage_type: a.current_coverage_type || '',
            aircraft_use: a.current_aircraft_use || '',
            // Per-row deductibles intentionally NOT seeded from AoP: those are
            // the EXPIRING policy's terms, not this quote's. Seeding them baked
            // stale deductibles into every new quote's Aircraft_on_Quote lines
            // whenever the broker only filled the header fields (post-mortem:
            // N8781P 2026-06-04 — Tower showed 250/2500 on a 100/500 quote).
            // Blank = inherit the quote header (QUOTE-ENTRY-SPEC §1.2:
            // per-aircraft OVERRIDES top-level; select-quote header-fallback
            // fills blanks at bind time).
            deductible_in_motion: '',
            deductible_not_in_motion: '',
            aircraft_premium: '',
          })));
          const hullSeed = {};
          for (const a of aircraft) {
            hullSeed[a.tail_number_id] = seedHull(a);
          }
          setHullByTail(hullSeed);
        } else {
          // Edit mode — same path for both surfaces: a manual scenario quote
          // is a regular CRM Quotation, so getQuote prefills it identically.
          const q = await getQuote(editQuotationId).catch(() => null);
          if (cancelled || !q) return;
          setCarrier(scenarioScope ? (scenarioScope.carrier?.name || q.carrier_name || '') : (q.carrier_name || ''));
          setUnderwriter(q.underwriter || '');
          setProposedEffective((q.proposed_effective || '').slice(0, 10));
          // COLUMN FIRST, PROSE AS FALLBACK. Rows written before these fields
          // had columns still carry the bracketed blocks, and this sheet must
          // not lose data on a row it did not write. Saving migrates them: the
          // value goes to its column and buildBrokerNotes stops re-emitting it.
          // Parsed HERE, above its first consumer, not next to the other
          // setters: a const referenced before its declaration is a TDZ crash,
          // which is how /apply went down for 20 hours (29888e6).
          const split = splitBrokerNotes(q.broker_notes || '');
          // The sheet used to carry a SECOND "quote expiration" input that wrote
          // [QExp] into prose. David 2026-07-29: it was a duplicate of "offer
          // expires", so the field is gone and quote_expires_at is the one date.
          // An old row's [QExp] therefore IS an offer expiry, and seeds the
          // column when it is empty rather than being dropped on the floor.
          // Two inputs for one date is how they diverge.
          setQuoteExpiresAt((q.quote_expires_at || '').slice(0, 10) || split.qExp || '');
          setAircraftUse(q.aircraft_usage || 'Pleasure & Business');
          setLiabPerOcc(q.liability_per_occurrence != null ? String(q.liability_per_occurrence) : '');
          setLiabPerPax(q.liability_per_passenger != null ? String(q.liability_per_passenger) : '');
          // Explicit flag wins; legacy rows (liability_smooth null) fall back to
          // inferring split from a stored per-passenger sublimit.
          setLiabilityMode(q.liability_smooth != null ? (q.liability_smooth ? 'smooth' : 'split') : (q.liability_per_passenger != null ? 'split' : 'smooth'));
          setMedical(q.medical_per_person != null ? String(q.medical_per_person) : '');
          setTerritory(q.territory || '');
          setCoverageEnhancements(q.coverage_enhancements || '');
          setInMotionDed(q.in_motion_deductible || '');
          setNotMotionDed(q.non_motion_deductible || '');
          setNpo(!!q.named_pilots_only);
          setOpwCert(q.opw_certificate || '');
          setOpwRatings(Array.isArray(q.opw_ratings) ? q.opw_ratings : []);
          setOpwTotalHours(q.opw_total_hours != null ? String(q.opw_total_hours) : '');
          setOpwMakeModelHours(q.opw_make_model_hours != null ? String(q.opw_make_model_hours) : '');
          setOpwTypeHours(q.opw_type_hours || '');
          {
            const parsed = parsePilotRequirements(q.pilot_requirements || '');
            // The column is canonical; a quote that never set it (null) shows
            // its legacy common text as one All-aircraft row, so nothing typed
            // before this existed is lost and saving promotes it.
            setCommonReqs(Array.isArray(q.common_requirements)
              ? q.common_requirements.map((r) => ({ tail_number: r?.tail_number || '', requirement: r?.requirement || '' }))
              : (parsed.common ? [{ tail_number: '', requirement: parsed.common }] : []));
            setPilotApr(parsed.byName);
          }
          // Scenario mode repopulates the canonical pilot×tail matrix directly.
          setAprEntries(Array.isArray(q.approved_pilot_requirements) ? q.approved_pilot_requirements : []);
          // Per-carrier pilot approval states (§4) → map keyed by contact_id.
          {
            const aps = Array.isArray(q.pilot_approval_states) ? q.pilot_approval_states : [];
            // Name-only exclusions (no contact id) are the wizard's to edit;
            // this sheet carries them through untouched, because it always
            // sends the whole array and would otherwise erase them.
            setNameOnlyStates(aps.filter((a) => a && !String(a.contact_id || '').trim()));
            setApprovalByPilot(Object.fromEntries(aps.filter((a) => a && String(a.contact_id || '').trim()).map((a) => [String(a.contact_id), {
              status: a.status || '', condition_text: a.condition_text || '', name: a.name || '',
              excluded_tails: Array.isArray(a.excluded_tails) ? a.excluded_tails.filter(Boolean) : [],
              // What the ROW said when it was read. encodeApprovalStatus needs
              // it to tell a value it is preserving from one just chosen: an
              // untouched conditional-with-no-text must not re-save as
              // approved. Never sent as a field.
              loaded_status: a.status || '',
            }])));
          }
          setOpwFullText(q.opw_full_text || '');
          setPremium(q.premium != null ? String(q.premium) : '');
          setHullPremium(q.hull_premium != null ? String(q.hull_premium) : '');
          setLiabilityPremium(q.liability_premium != null ? String(q.liability_premium) : '');
          setTriaPremium(q.tria_premium != null ? String(q.tria_premium) : '');
          setEstimatedTaxes(q.estimated_taxes != null ? String(q.estimated_taxes) : '');
          setClientVisible(!!q.client_visible);
          setBrokerRecommended(!!q.broker_recommended);
          setBaseNotes(split.baseNotes);
          setCarrierRef(q.carrier_quote_number || split.ref);
          setQuoteDate(split.qDate);
          // The column, else the legacy [Term: N] tag when the notes carry one,
          // else nothing (decision 14): splitBrokerNotes answers '12' for notes
          // with no tag, and an edit must never show a 12 nobody picked.
          setTermMonths(q.term_months != null ? termSeed(q.term_months) : (/\[Term:/.test(q.broker_notes || '') ? split.term : ''));
          setSubjectivities(q.subject_to || split.subjectivities);
          // Subform rows — preserve row.id end-to-end so Zoho doesn't
          // duplicate the subform on PATCH (CLAUDE.md recurring bug).
          const aoq = Array.isArray(q.aircraft_on_quote) ? q.aircraft_on_quote : [];
          // Policy-level coverage type: prefer the quote header scalar; fall back
          // to the first aircraft's row (quotes saved before the header existed).
          setCoverageType(q.coverage_type || aoq[0]?.Coverage_Type || '');
          const prefillByTail = {};
          for (const a of pf?.aircraft || []) prefillByTail[a.tail_number_id] = a;
          const hullSeed = {};
          const sched = aoq.map((row) => {
            const tid = row.Tail_Number?.id || row.Tail_Number || null;
            const ref = prefillByTail[tid] || {};
            const hullValStr = row.Hull_Value != null ? String(row.Hull_Value) : '';
            if (tid) hullSeed[tid] = hullValStr;
            return {
              id: row.id || undefined,
              tail_number_id: tid,
              tail_display: row.Tail_Number?.name || ref.tail_display || '',
              year: row.Year || ref.year || null,
              make_model: row.Make_Model || ref.make_model || '',
              hull_value: hullValStr,
              coverage_type: row.Coverage_Type || '',
              aircraft_use: row.Aircraft_Use || '',
              deductible_in_motion: row.Deductible_In_Motion || '',
              deductible_not_in_motion: row.Deductible_Not_In_Motion || '',
              aircraft_premium: row.Aircraft_Premium != null ? String(row.Aircraft_Premium) : '',
            };
          });
          setSchedule(sched);
          setHullByTail(hullSeed);
          const cp = Array.isArray(q.coverage_parts) ? q.coverage_parts : [];
          // Shape-tolerant: coverage_parts round-trip as snake_case jsonb, but
          // some read paths still hand back the CRM-mirror PascalCase shape.
          setCoverageParts(cp.map((row) => {
            const pctOf = row.premium_pct_of || '';
            const premium = row.premium ?? row.Premium;
            return {
              id: row.id || undefined,
              key: row.key || undefined,
              part_type: row.part_type || row.Part_Type || '',
              limit: (row.limit ?? row.Limit) != null ? String(row.limit ?? row.Limit) : '',
              sub_limit: (row.sub_limit ?? row.Sub_Limit) != null ? String(row.sub_limit ?? row.Sub_Limit) : '',
              broker_notes: row.broker_notes || row.Broker_Notes || '',
              client_blurb: row.client_blurb || '',
              optional: (row.optional ?? row.Optional) === true,
              elected_default: row.elected_default === true,
              price_mode: pctOf || 'flat',
              pct: pctOf && row.pct != null ? String(Number(row.pct) * 100) : '',
              premium: !pctOf && premium != null ? String(premium) : '',
            };
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, dealId, quotationId, scenarioScope]);

  // Per-row hull edits propagate into schedule too (so the on-save
  // builder doesn't need two reconciliation paths).
  function updateHull(tailId, value) {
    setHullByTail((prev) => ({ ...prev, [tailId]: value }));
    setSchedule((rows) =>
      rows.map((r) =>
        r.tail_number_id === tailId ? { ...r, hull_value: value } : r
      )
    );
  }

  function updateScheduleRow(idx, patch) {
    setSchedule((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );
    if (patch.hull_value !== undefined) {
      const tid = schedule[idx]?.tail_number_id;
      if (tid) setHullByTail((p) => ({ ...p, [tid]: patch.hull_value }));
    }
  }

  function addCoveragePart() {
    setCoverageParts((rows) => [
      ...rows,
      { part_type: PART_TYPE_PICKLIST[0], limit: '', sub_limit: '', broker_notes: '', client_blurb: '', optional: false, elected_default: false, price_mode: 'flat', premium: '', pct: '' },
    ]);
  }
  function updateCoveragePart(idx, patch) {
    setCoverageParts((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeCoveragePart(idx) {
    setCoverageParts((rows) => rows.filter((_, i) => i !== idx));
  }

  function toggleRating(r) {
    setOpwRatings((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  }

  // ── Premium-breakdown validation ─────────────────────────────────
  const premiumValidation = useMemo(() => {
    const tot = toNumber(premium);
    const hp = toNumber(hullPremium);
    const lp = toNumber(liabilityPremium);
    const tp = toNumber(triaPremium);
    if (tot == null) return null;
    if (hp == null && lp == null && tp == null) return null;
    const sum = (hp || 0) + (lp || 0) + (tp || 0);
    const delta = Math.abs(sum - tot);
    if (delta > 50) {
      return {
        kind: 'warn',
        message: `Hull + Liability + TRIA = ${fmtMoney(sum)} vs Total ${fmtMoney(tot)} (Δ ${fmtMoney(delta)})`,
      };
    }
    return { kind: 'ok', message: `Breakdown matches total within ${fmtMoney(delta)}` };
  }, [premium, hullPremium, liabilityPremium, triaPremium]);

  // OPW soft-warn (§2.5): a partially-filled structured warranty can understate
  // what the client sees on the Tower. The trigger case showed only "25TW" while
  // the carrier actually required 300 total / 25 TW / 15 make&model. Non-blocking
  // nudge (mirrors premiumValidation) — it never gates Save.
  const opwWarning = useMemo(() => {
    if (npo) return null;
    const hasType = !!String(opwTypeHours).trim();
    const hasMM = !!String(opwMakeModelHours).trim();
    const hasTotal = !!String(opwTotalHours).trim();
    // Most common understating bug: a type/make-model floor set with no total-time
    // floor. Carriers almost always require a total minimum alongside.
    if ((hasType || hasMM) && !hasTotal) {
      return 'Type or make/model hours are set with no total-hours floor.';
    }
    // Cross-check: the full clause mentions hour figures that aren’t broken out
    // into the structured fields the Tower actually renders.
    const ft = String(opwFullText).trim();
    const anyStructured = hasType || hasMM || hasTotal || !!opwCert || opwRatings.length > 0;
    if (ft && anyStructured) {
      const ftNums = (ft.match(/\d+/g) || []).map(Number).filter((n) => n >= 5);
      const structuredNums = [opwTotalHours, opwMakeModelHours, opwTypeHours]
        .flatMap((v) => (String(v).match(/\d+/g) || []).map(Number));
      const uncaptured = [...new Set(ftNums.filter((n) => !structuredNums.includes(n)))];
      if (uncaptured.length) {
        return `The full clause mentions ${uncaptured.slice(0, 3).join(', ')}, which are not in the fields above. The client sees only the fields.`;
      }
    }
    return null;
  }, [npo, opwTypeHours, opwMakeModelHours, opwTotalHours, opwFullText, opwCert, opwRatings]);

  // ── Required-fields gate ─────────────────────────────────────────
  // missingRequired drives both the gate and the footer hint that says WHY
  // Save is disabled (plain derivation — cheap, no memo needed).
  const missingRequired = [];
  if (!carrier.trim()) missingRequired.push('a carrier');
  if (toNumber(premium) == null) missingRequired.push('a premium');
  if (!aircraftUse.trim()) missingRequired.push('an aircraft use');
  if (!proposedEffective.trim()) missingRequired.push('an effective date');
  if (termMonths !== '' && termMonthsOf(termMonths) == null) missingRequired.push(`a term of 1 to ${TERM_MAX_MONTHS} months`);
  const canSave = !submitting && missingRequired.length === 0;

  function buildPayload({ markVisible = false } = {}) {
    // NO DEFAULT (S6, decision 12). This was `coverageType || 'Full Flight'`, so
    // a quote nobody typed a coverage type into was saved as Full Flight and
    // passed the present gate's first item. Unstated stays unstated.
    const headerCoverage = coverageType || '';
    const eligibleRows = schedule.filter((r) => r.tail_number_id);
    // Single aircraft → the header coverage type IS the aircraft term (no per-row
    // control was shown). 2+ aircraft → per-row override, with unset rows
    // inheriting the header. A row carries coverage_type only when one is stated.
    const useCoverageOverride = eligibleRows.length >= 2;
    const sched = eligibleRows
      .map((r) => {
        const out = { tail_number_id: r.tail_number_id };
        if (r.id) out.id = r.id;
        if (r.make_model) out.make_model = r.make_model;
        if (r.year) out.year = Number(r.year) || undefined;
        const h = toNumber(r.hull_value);
        if (h != null) out.hull_value = h;
        // A stated type only: the row's own, else the header's. Never a default.
        const ct = useCoverageOverride ? (r.coverage_type || headerCoverage) : headerCoverage;
        if (ct) out.coverage_type = ct;
        if (r.aircraft_use) out.aircraft_use = r.aircraft_use;
        if (r.deductible_in_motion) out.deductible_in_motion = r.deductible_in_motion;
        if (r.deductible_not_in_motion) out.deductible_not_in_motion = r.deductible_not_in_motion;
        const ap = toNumber(r.aircraft_premium);
        if (ap != null) out.aircraft_premium = ap;
        return out;
      });
    const parts = coverageParts
      .filter((r) => r.part_type)
      .map((r) => {
        const out = { part_type: r.part_type, key: coveragePartKey(r) };
        if (r.id) out.id = r.id;
        const l = toNumber(r.limit);
        if (l != null) out.limit = l;
        const s = toNumber(r.sub_limit);
        if (s != null) out.sub_limit = s;
        if (r.broker_notes) out.broker_notes = r.broker_notes;
        if (r.client_blurb) out.client_blurb = r.client_blurb;
        // Part A — election + pricing. Optional parts render as client-electable
        // checkboxes; flat or percentage-of-option pricing (TRIA = % of the
        // active option's liability premium).
        out.optional = r.optional === true;
        if (r.optional) out.elected_default = r.elected_default === true;
        if (r.price_mode === 'liability_premium' || r.price_mode === 'hull_premium') {
          out.premium_pct_of = r.price_mode;
          const p = toNumber(r.pct);
          out.pct = p != null ? p / 100 : 0;
        } else {
          const prem = toNumber(r.premium);
          if (prem != null) out.premium = prem;
        }
        return out;
      });
    const combinedNotes = buildBrokerNotes({ baseNotes, qDate: quoteDate });
    return {
      deal_id: dealId,
      carrier: carrier.trim(),
      underwriter: underwriter.trim() || undefined,
      premium: toNumber(premium),
      // Aircraft USE → quotes.aircraft_use → Quotation.Aircraft_Usage.
      aircraft_usage: aircraftUse,
      // Policy-level hull-coverage scope → quote_options.coverage_type →
      // Quotation.Coverage_Type. Also propagated onto each schedule row above
      // (single aircraft = header is the term; 2+ = header is the default).
      coverage_type: headerCoverage || undefined,
      proposed_effective: proposedEffective || undefined,
      // Always sent (even '') so clearing the field on edit clears the lapse
      // date too ('' → NULL server-side).
      quote_expires_at: quoteExpiresAt,
      liability_per_occurrence: toNumber(liabPerOcc),
      liability_per_passenger: liabilityMode === 'split' ? toNumber(liabPerPax) : null,
      // Explicit smooth flag — per-passenger null alone is ambiguous (legacy rows
      // predate the column), and the mirror needs it to write Liability_Smooth and
      // CLEAR a stale sublimit in CRM (Sonic Bird post-mortem 2026-07-10).
      liability_smooth: liabilityMode === 'smooth',
      // Always sent (even '') so clearing the field on edit clears CRM too.
      // Domain column semantics: '' = explicitly cleared, NULL = legacy/unknown.
      coverage_enhancements: coverageEnhancements.trim(),
      medical_per_person: toNumber(medical),
      territory: territory.trim() || undefined,
      named_pilots_only: npo,
      in_motion_deductible: inMotionDed || undefined,
      non_motion_deductible: notMotionDed || undefined,
      opw_certificate: npo ? undefined : (opwCert || undefined),
      opw_ratings: npo ? undefined : (opwRatings.length ? opwRatings : undefined),
      opw_total_hours: npo ? undefined : toNumber(opwTotalHours),
      opw_make_model_hours: npo ? undefined : toNumber(opwMakeModelHours),
      opw_type_hours: npo ? undefined : (opwTypeHours.trim() || undefined),
      // Standalone mode: legacy common/per-pilot text. Scenario mode: omit —
      // the backend generates pilot_requirements as a rollup of the matrix.
      pilot_requirements: scenarioScope ? undefined : (() => {
        // Offerable only: a requirement block for a name the quote cannot name
        // is a claim with no subject (2026-07-31 roster merge).
        const encoded = encodePilotRequirements(buildCommonRollup(commonReqs), pilotApr, approvalPilots);
        return encoded || undefined;
      })(),
      // Scenario mode only: the canonical pilot×tail APR matrix. Always sent
      // (even empty) so removing every requirement on edit clears the record.
      // Shared requirements, structured. Sent ALWAYS in standalone mode,
      // including as an empty array: [] is the broker clearing the shared block
      // and is a different answer from never having set one (NULL). Scenario
      // mode omits it, as it omits pilot_requirements, because the backend
      // builds both from the matrix there.
      common_requirements: scenarioScope ? undefined : commonReqs
        .map((r) => ({
          tail_number: String(r?.tail_number || '').trim().toUpperCase(),
          requirement: String(r?.requirement || '').trim(),
        }))
        .filter((r) => r.requirement),
      approved_pilot_requirements: scenarioScope
        ? aprEntries
            .filter((e) => e && e.contact_id && (e.requirement || '').trim())
            .map((e) => ({
              contact_id: String(e.contact_id),
              name: e.name || '',
              tail_number: (e.tail_number || '').trim().toUpperCase(),
              requirement: e.requirement.trim(),
            }))
        : undefined,
      // Per-carrier pilot approval decisions (§4). Always sent (even empty) so
      // clearing a decision on edit persists. Only pilots with a status emit.
      // ONE per-pilot requirement box here too, and it is the one that PRINTS.
      // 4370c82 fixed this in the wizard and left the decoy standing in THIS
      // sheet: a single-line condition_text input rendered FIRST, with the
      // placeholder that describes exactly what a broker pastes, above the
      // textarea that actually reaches the binding application. condition_text
      // is now a MIRROR written FROM the requirement, so the two cannot
      // disagree, and an EMPTY box preserves what is stored rather than
      // erasing it: live quotes carry text there and nowhere else.
      pilot_approval_states: Object.entries(approvalByPilot)
        .filter(([, v]) => v && v.status)
        .map(([contact_id, v]) => {
          // Scenario mode types requirements into the pilot x tail matrix, not
          // here, so pilotApr is empty and the stored value is preserved.
          const req = (pilotApr[v.name || ''] || '').trim();
          const cond = req || (v.condition_text || '').trim();
          const status = encodeApprovalStatus({ ...v, condition_text: cond });
          const tails = (status === 'excluded' || status === 'declined') ? [] : (v.excluded_tails || []).filter(Boolean);
          return {
            contact_id: String(contact_id),
            name: v.name || '',
            tail_number: '',
            status,
            condition_text: cond || undefined,
            ...(tails.length ? { excluded_tails: tails } : {}),
          };
        })
        .concat(nameOnlyStates),
      opw_full_text: npo ? undefined : (opwFullText.trim() || undefined),
      hull_premium: toNumber(hullPremium),
      liability_premium: toNumber(liabilityPremium),
      tria_premium: toNumber(triaPremium),
      estimated_taxes: toNumber(estimatedTaxes),
      // Canonical term field (SIX-MONTH-TERM-SPEC §5.1). The [Term: N months]
      // broker_notes tag above stays for one release (belt and suspenders);
      // 'Other' parses to NaN → undefined → field untouched.
      term_months: termMonthsOf(termMonths) ?? undefined,
      // To their COLUMNS, not into broker_notes. mapBodyToQuote already read
      // carrier_quote_number; the sheet simply never sent it.
      carrier_quote_number: carrierRef.trim() || undefined,
      // Broker-internal requirements before bind ("signed SNKL; loss runs").
      // Always sent, even '', so clearing the textarea clears the column.
      subject_to: subjectivities.trim(),
      broker_notes: combinedNotes || undefined,
      client_visible: clientVisible || markVisible || false,
      // B14, logging only, and only once touched (strict boolean).
      ...(!savedQuoteId && typeof irOpwOnly === 'boolean' ? { ir_opw_only: irOpwOnly } : {}),
      broker_recommended: brokerRecommended,
      aircraft_schedule: sched.length ? sched : undefined,
      coverage_parts: parts.length ? parts : undefined,
    };
  }

  async function doSave({ markVisible = false, present = false } = {}) {
    if (!canSave) return null;
    setSubmitting(true);
    try {
      const payload = buildPayload({ markVisible: markVisible || present });
      let resultId = savedQuoteId;
      let saved = null; // the save door's answer: client_visible, present_refused, present_missing
      // quotes.carrier is set by the resolver at CREATE and is never sent by an
      // edit. It is a SNAPSHOT of what the carrier was called when the quote was
      // written, not a field a human retypes on an existing row, and it is also
      // the identity the scenario cell keys on. Changing which carrier a quote
      // belongs to is not an edit; it is a different quote.
      if (resultId) { delete payload.carrier; delete payload.carrier_slug; }
      if (scenarioScope) {
        // Cell-scoped save → the 4a scenario-quote routes. They stamp
        // quotes.total_premium and own the CRM-write traps (AoQ row-id
        // preservation, toCRMDateOnly, Tail_Number {id} lookup). In this
        // mode savedQuoteId holds the Supabase quotes-row id (the PATCH
        // target), NOT the CRM Quotation id.
        delete payload.deal_id;
        // On CREATE only. This used to run on every save and rewrote the row's
        // own carrier from the slug to the display name, which was the dedup key
        // before item 3 moved it to carrier_id.
        if (!resultId) {
          payload.carrier = scenarioScope.carrier?.name || payload.carrier;
          payload.carrier_slug = scenarioScope.carrier?.slug || undefined;
        }
        if (resultId) {
          saved = await patchScenarioQuote(scenarioScope.submissionId, scenarioScope.scenarioId, resultId, payload);
        } else {
          saved = await createScenarioQuote(scenarioScope.submissionId, scenarioScope.scenarioId, payload);
          resultId = saved?.quote_id || null;
          setSavedQuoteId(resultId);
        }
      } else if (resultId) {
        saved = await patchQuote(resultId, payload);
      } else {
        saved = await logQuoteReceived(payload);
        resultId = saved?.id || null;
        setSavedQuoteId(resultId);
      }
      // Present is only attempted off the deal-level sheet (never scenario
      // mode — resultId there is a quotes-row id, not a CRM Quotation id, and
      // the present button isn't rendered). Track whether it ACTUALLY ran so
      // the toast and the onSaved hand-off never claim "presented" on a failure
      // (the old code swallowed the throw to console.warn and still fired the
      // green success toast — a real failure read as success).
      // THE SAVE ANSWER SAYS WHAT THE CLIENT SEES (S5, decision 13). A save
      // that asked to show and was refused by the present gate saved the quote
      // and showed nothing; the list is the server's, on screen, never the
      // console. The id is adopted above, so a retry edits rather than creates.
      const { refused, stillShown } = presentSaveNotices(saved);
      const visible = saved?.client_visible === true;
      let presented = false;
      if (present && resultId && !scenarioScope && !refused) {
        try {
          await presentQuote(resultId);
          presented = true;
        } catch (e) {
          const pmsg = e instanceof ApiError ? (e.body?.error || e.code) : (e.message || 'failed');
          toast.error(e instanceof ApiError && e.body?.code === 'QUOTE_NOT_PRESENTABLE'
            ? `Saved, not shown to the client. ${pmsg}`
            : `Saved, but present failed: ${pmsg}`, { duration: Infinity });
        }
      }
      if (refused) toast.error(refused, { duration: Infinity });
      if (stillShown) toast.info(stillShown);
      if (presented) {
        toast.success('Quote presented');
      } else if (!(present && !scenarioScope)) {
        // present wasn't requested (or scenario mode): the normal save toast,
        // naming visibility only when the answer says the client sees it.
        toast.success(markVisible && visible && !refused ? 'Quote saved & marked client-visible' : 'Quote saved');
      }
      const sessionToken = prefill?.deal?.session_token || null;
      if (typeof onSaved === 'function') {
        onSaved({
          id: resultId,
          // Reflect the ACTUAL outcome — the 'quotes-ready' compose hand-off
          // (handleQuoteSaved) only fires when the quote really presented.
          present: presented,
          markVisible: markVisible && visible,
          sessionToken,
        });
      }
      // Close the sheet in every case. Save+Present hand-off opens
      // ComposeSheet at the PARENT level (see DealActionsSheets) so
      // we don't stack Sheet-on-Sheet (Vision §V2 fix #6 handoff
      // decision 2026-05-20).
      onOpenChange?.(false);
      return resultId;
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.body?.error || err.code
        : err.message || 'failed';
      toast.error(`Save failed: ${msg}`);
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  // THERE IS NO SIX-MONTH CONTROL IN THIS SHEET, AND THAT IS DELIBERATE
  // (2026-07-31). startSixMonthClone used to flip this sheet into new-quote mode
  // as a clone of the annual, creating a SECOND QUOTE ROW to carry one different
  // number. The two copies were then free to disagree on coverage, which is how
  // Black Bear Aero ended with a 12-month row at hull $160,000 beside a
  // six-month one at $155,000.
  //
  // A six-month offer is a second PREMIUM under one quote. The quote wizard's
  // offer step is the only door (5c7cb68). A broker who is in this sheet closes
  // it and opens the wizard on that quote. This sheet has no option-level
  // surface at all, so adding one here would be a second door to the same thing.

  // In scenario mode, scope the rendered aircraft to the submission's
  // included tails — schedule rows are seeded the same way, so a hull input
  // for an off-roster tail would silently not save. Tail-format mismatch →
  // keep all rather than render an empty schedule.
  const aircraft = (() => {
    const all = prefill?.aircraft || [];
    if (!scenarioScope?.includedTails?.length) return all;
    const inc = new Set(scenarioScope.includedTails);
    const scoped = all.filter((a) => inc.has(normTail(a.tail_display)));
    return scoped.length ? scoped : all;
  })();
  const pilots = prefill?.pilots || [];
  const multiAircraft = aircraft.length > 1;

  // ── Scenario-mode APR matrix (pilot×tail) ────────────────────────────
  // Roster is the submission's rated pilots (passed via scenarioScope), NOT
  // the deal's CRM Approved_Pilots. Tail options come from the scoped schedule.
  const ratedPilots = scenarioScope?.ratedPilots || [];
  const tailOptions = (() => {
    const fromSchedule = (schedule || []).map((s) => (s.tail_display || '').trim().toUpperCase()).filter(Boolean);
    const fromScope = (scenarioScope?.includedTails || []).map((t) => String(t).trim().toUpperCase());
    return [...new Set([...fromSchedule, ...fromScope])];
  })();
  const singleTail = tailOptions.length === 1 ? tailOptions[0] : '';
  const addAprEntry = () => setAprEntries((prev) => [...prev, {
    contact_id: ratedPilots[0]?.contact_id || '',
    name: ratedPilots[0]?.name || '',
    tail_number: singleTail || (tailOptions[0] || ''),
    requirement: '',
  }]);
  const updateAprEntry = (idx, patch) => setAprEntries((prev) =>
    prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const removeAprEntry = (idx) => setAprEntries((prev) => prev.filter((_, i) => i !== idx));
  const setAprPilot = (idx, contactId) => {
    const p = ratedPilots.find((rp) => String(rp.contact_id) === String(contactId));
    updateAprEntry(idx, { contact_id: contactId, name: p?.name || '' });
  };

  // ── Named pilots — carrier approval (§4) ─────────────────────────────
  // Pilot list: the rated roster in cockpit mode, the deal's pilots otherwise.
  // Plus any pilot that already carries a stored decision but isn't in that list
  // (e.g. removed from the roster) so it stays visible/editable and round-trips.
  // Offerable only. The prefill's pilot list now carries the WHOLE roster,
  // including pilots with no CRM Contact (2026-07-31), and String(null) would
  // key a decision row on the literal "null" and save it. They are rendered by
  // name further down; nothing is written against them.
  const approvalPilots = (scenarioScope ? ratedPilots : pilots).filter((p) => p && p.offerable !== false);
  const approvalRows = (() => {
    const list = approvalPilots.map((p) => ({ contact_id: String(p.contact_id), name: p.name || '' }));
    const seen = new Set(list.map((p) => p.contact_id));
    for (const [cid, v] of Object.entries(approvalByPilot)) {
      if (!seen.has(cid)) list.push({ contact_id: cid, name: v.name || '' });
    }
    return list;
  })();
  const setApproval = (contactId, name, patch) => setApprovalByPilot((m) => ({
    ...m,
    [String(contactId)]: { status: '', condition_text: '', name, ...m[String(contactId)], ...patch },
  }));
  // Names that can carry a SAVABLE per-pilot requirement. encodePilotRequirements
  // (buildPayload) only emits blocks for prefill `pilots`, keyed by name — so the
  // merged matrix shows the requirement field only for those, keeping the saved
  // pilot_requirements payload byte-equivalent. A stored-decision-only pilot
  // (removed from the roster) still shows its decision, just no requirement box.
  // Roster pilots this quote cannot name. Rendered by name with the reason;
  // nothing about the quote is written against them.
  const unofferablePilots = (scenarioScope ? [] : pilots).filter((p) => p && p.offerable === false);

  // One-click "move the 'applies to all' text into a specific pilot's block."
  // Broker-driven — no name parsing — so it's immune to the Jerry/Jeremy and
  // II/III/Jr. drift that makes automatic name-matching unreliable. The marker
  // the save step emits ([Pilot.name]) is matched at bind time against the same
  // CRM Pilot.name, so it round-trips exactly. Appends if the pilot already has
  // specific text.
  // Moves EVERY shared row into that pilot's block, which is what the chip
  // meant when there was only one string. A row that belongs to one pilot was
  // never shared in the first place.
  const assignCommonToPilot = (name) => {
    const text = commonReqs.map((r) => (r.requirement || '').trim()).filter(Boolean).join('\n\n');
    if (!text) return;
    setPilotApr((prev) => {
      const existing = (prev[name] || '').trim();
      return { ...prev, [name]: existing ? `${existing}\n\n${text}` : text };
    });
    setCommonReqs([]);
    toast.success(`Moved to ${name}`);
  };

  const setCommonReq = (i, patch) => setCommonReqs(commonReqs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  // The shared text as one string, for the suggestion scan and the count chips.
  const commonText = commonReqs.map((r) => (r.requirement || '').trim()).filter(Boolean).join('\n\n');

  // Passive suggestion ONLY (broker confirms by clicking) — flag pilots whose
  // name token appears in the "applies to all" text. Catches the common case
  // (carrier prose names one pilot, usually by last name). Suffixes/numerals
  // are excluded so "III"/"Jr" never false-match. Never auto-applies, so a
  // wrong guess is harmless: it just highlights a chip the broker can ignore.
  const aprLower = commonText.toLowerCase();
  const suggestedAprPilots = commonText.trim()
    ? approvalPilots.filter((p) =>
        (p.name || '').split(/\s+/).some((tok) => {
          const t = tok.replace(/[^a-z]/gi, '').toLowerCase();
          return t.length >= 3 && !['iii', 'jnr', 'snr'].includes(t)
            && new RegExp(`\\b${t}\\b`).test(aprLower);
        })
      )
    : [];
  const suggestedAprSet = new Set(suggestedAprPilots.map((p) => p.contact_id));
  const headerTitle = scenarioScope
    ? `${scenarioScope.carrier?.name || 'Manual quote'} — ${scenarioScope.scenario?.scenario_label || '(unlabeled)'}`
    : quotationId ? 'Edit quote' : 'New quote';
  const headerSub = scenarioScope
    ? `${scenarioScope.crmQuotationId ? 'Edit' : 'New'} manual quote on this scenario cell · ${prefill?.account?.name || prefill?.deal?.name || '…'}`
    : prefill?.account?.name || prefill?.deal?.name || '…';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex max-h-[96vh] flex-col overflow-hidden md:mx-auto md:max-w-3xl"
      >
        <SheetHeader>
          <SheetTitle>
            {headerTitle}: {headerSub}
          </SheetTitle>
          <SheetDescription>
            {loading
              ? 'Loading deal context…'
              : 'Required: carrier · premium · aircraft use · effective date. Fill what you have; expand sections for more detail.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 pr-1 space-y-4">
          {/* ── REQUIRED — always visible, gates Save ─────────────── */}
          <fieldset className="rounded-card border border-border bg-surface-strong p-3">
            <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-accent">
              Required
            </legend>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Carrier *">
                <Input
                  autoFocus={!scenarioScope && !savedQuoteId}
                  disabled={!!scenarioScope || !!savedQuoteId}
                  list="qes-carrier-list"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="Beacon, Global Aerospace, Starr, USAIG…"
                />
                {scenarioScope
                  ? <Helper>Locked to this matrix column.</Helper>
                  : savedQuoteId
                    ? <Helper>Recorded when this quote was logged. A different carrier is a different quote.</Helper>
                    : null}
                <datalist id="qes-carrier-list">
                  {carriers.map((c) => (
                    <option key={c.id} value={c.underwriter || c.vendor_name}>
                      {c.vendor_name}
                    </option>
                  ))}
                </datalist>
              </Field>
              <Field label="Premium ($) *">
                <MoneyInput
                  value={premium}
                  onChange={(v) => setPremium(v)}
                  placeholder="12,345"
                />
              </Field>
              {/* Estimated taxes / surplus lines — surfaced next to Premium
                  because it's the one breakdown field the broker routinely
                  enters (the rest stay in the optional Premium breakdown
                  section). Manual entry — no auto-calc. Writes estimated_taxes
                  unchanged, so downstream payment math (premium + taxes + fee)
                  is untouched. */}
              <Field label="Estimated taxes / surplus lines">
                <MoneyInput
                  value={estimatedTaxes}
                  onChange={setEstimatedTaxes}
                  placeholder="0"
                />
                <Helper>Surplus-lines / state taxes, if any. Manual — leave blank when none apply.</Helper>
              </Field>
              <Field label="Usage *">
                {/* This is aircraft USAGE (Pleasure & Business, Flying Club,
                    Commercial, ...), NOT hull-coverage scope. Coverage Type is
                    per-aircraft in the schedule section below. → quotes.aircraft_use
                    → Quotation.Aircraft_Usage. */}
                <select
                  value={aircraftUse}
                  onChange={(e) => setAircraftUse(e.target.value)}
                  className={inputClass}
                >
                  <ListOptions field="aircraft_use" stored={aircraftUse} />
                </select>
              </Field>
              <Field label="Effective date *">
                {/* Real calendar picker (Popover month grid) — the native
                    <input type=date> rendered as bare steppers with no obvious
                    calendar affordance on macOS/Safari. Value stays YYYY-MM-DD
                    so proposed_effective plumbing is unchanged. Ring-warns when
                    empty — this is the gate brokers miss (edit-opened quotes can
                    arrive with no proposed_effective). */}
                <DatePicker
                  value={proposedEffective}
                  onChange={setProposedEffective}
                  invalid={!proposedEffective.trim()}
                  placeholder="Pick the effective date"
                />
                {!proposedEffective.trim() ? (
                  // Name WHY it's blank on a renewal so the broker reads it as a
                  // data gap, not a broken picker (spec item 1b#3).
                  prefill?.deal?.default_effective ? (
                    <Helper>Required before Save — expected effective {prefill.deal.default_effective}.</Helper>
                  ) : prefill?.deal?.is_renewal ? (
                    <Helper>No policy start on the Deal yet — set Policy Start on the Deal, or pick the effective date manually.</Helper>
                  ) : (
                    <Helper>Required before Save.</Helper>
                  )
                ) : prefill?.deal?.default_effective ? (
                  <Helper>
                    Default = {prefill.deal.effective_source === 'prior_policy_end'
                      ? 'prior policy expiration'
                      : 'expected effective'} ({prefill.deal.default_effective}).
                  </Helper>
                ) : null}
              </Field>
              {/* MV-03 §2.3 — offer lapse date; drives the derived
                  "offer expires" chips (panel + follow-ups). Optional. */}
              <Field label="Offer expires">
                <DatePicker
                  value={quoteExpiresAt}
                  onChange={setQuoteExpiresAt}
                  placeholder="When the carrier's offer lapses"
                />
                {quoteExpiresAt ? <Helper>Chips surface if this passes unactioned.</Helper> : null}
              </Field>
            </div>
          </fieldset>

          {/* ── COVERAGE — collapsible (default-open on desktop) ──── */}
          <Section
            title="Coverage"
            open={openSections.headline}
            onToggle={() => toggle('headline')}
            primary
          >
            <div className="space-y-3">
              {/* Liability */}
              <fieldset className="rounded-card border border-border bg-bg/30 p-3">
                <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                  Liability
                </legend>
                <div className="mb-2 inline-flex gap-1 rounded-pill bg-bg p-0.5">
                  {['smooth', 'split'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setLiabilityMode(m)}
                      className={cn(
                        'rounded-pill px-4 py-1.5 text-xs font-medium uppercase tracking-wide',
                        liabilityMode === m
                          ? 'bg-accent text-white'
                          : 'text-text-muted hover:text-text'
                      )}
                    >
                      {m === 'smooth' ? 'Smooth (CSL)' : 'Split'}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label={liabilityMode === 'smooth' ? 'Per occurrence (CSL)' : 'Per occurrence'}>
                    <MoneyInput value={liabPerOcc} onChange={setLiabPerOcc} placeholder="1,000,000" />
                    <div className="mt-1 flex gap-1">
                      {LIAB_OCC_QUICK.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setLiabPerOcc(String(v))}
                          className="rounded-pill border border-border bg-bg px-2 py-0.5 text-[10px] text-text-muted hover:border-border-strong"
                        >
                          {fmtMoney(v)}
                        </button>
                      ))}
                    </div>
                  </Field>
                  {liabilityMode === 'split' ? (
                    <Field label="Per passenger">
                      <MoneyInput value={liabPerPax} onChange={setLiabPerPax} placeholder="100,000" />
                      <div className="mt-1 flex gap-1">
                        {LIAB_PAX_QUICK.map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setLiabPerPax(String(v))}
                            className="rounded-pill border border-border bg-bg px-2 py-0.5 text-[10px] text-text-muted hover:border-border-strong"
                          >
                            {fmtMoney(v)}
                          </button>
                        ))}
                      </div>
                    </Field>
                  ) : null}
                  <Field label="Medical payments per person">
                    <MoneyInput value={medical} onChange={setMedical} placeholder="5,000" />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {MEDICAL_QUICK.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setMedical(String(m))}
                          className="rounded-pill border border-border bg-bg px-2 py-0.5 text-[10px] text-text-muted hover:border-border-strong"
                        >
                          {fmtMoney(m)}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </fieldset>

              {/* Hull values */}
              {aircraft.length === 0 ? (
                <p className="rounded-card border border-border bg-bg/30 p-2 text-[11px] text-text-dim">
                  No aircraft on this deal yet. Hull values will populate once aircraft are added to <code>Aircraft_on_Policy</code>.
                </p>
              ) : !multiAircraft ? (
                <Field label="Hull value">
                  <MoneyInput
                    value={hullByTail[aircraft[0].tail_number_id] || ''}
                    onChange={(v) => updateHull(aircraft[0].tail_number_id, v)}
                    placeholder="85,000"
                  />
                  <Helper>
                    {aircraft[0].tail_display} · {aircraft[0].year} {aircraft[0].make_model}
                  </Helper>
                </Field>
              ) : (
                <fieldset className="rounded-card border border-border bg-bg/30 p-3">
                  <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                    Hull values (per aircraft)
                  </legend>
                  <ul className="space-y-2">
                    {aircraft.map((a) => (
                      <li key={a.tail_number_id} className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-text">{a.tail_display}</div>
                          <div className="truncate text-[10px] text-text-dim">{a.year} {a.make_model}</div>
                        </div>
                        <MoneyInput
                          value={hullByTail[a.tail_number_id] || ''}
                          onChange={(v) => updateHull(a.tail_number_id, v)}
                          placeholder="hull $"
                          className="w-32"
                        />
                      </li>
                    ))}
                  </ul>
                </fieldset>
              )}

              {/* Coverage type — policy-level hull-coverage scope. This IS the
                  aircraft term for a single-aircraft policy; with 2+ aircraft it
                  is the default and each schedule row can override it below.
                  Writes body.coverage_type → quote_options.coverage_type →
                  Quotation.Coverage_Type, and is propagated onto every schedule
                  row in buildPayload so Aircraft_on_Quote/Policy carry it too. */}
              <Field label="Coverage type">
                <select
                  value={coverageType}
                  onChange={(e) => setCoverageType(e.target.value)}
                  className={inputClass}
                >
                  {/* NOT STATED IS A STATE (S6, decision 12). This read
                      'Full Flight' for a quote that never said, and the save
                      wrote it, so the gate's first item could never fire. */}
                  <option value="">not stated</option>
                  <ListOptions field="coverage_type" stored={coverageType} />
                </select>
                <Helper>{schedule.length >= 2 ? 'Default for all aircraft; override per aircraft in the schedule below.' : 'Hull-coverage scope for this policy.'}</Helper>
              </Field>

              {/* Deductibles — Not-in-motion left, In-motion right (broker reads
                  them ground→flight, low→high). DOM order only; payload keys
                  (in_motion_deductible / non_motion_deductible) unchanged. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Not-in-motion deductible">
                  {/* Plain text (mirrors in-motion): the CRM Non_Motion_Deductible
                      field is `text`, so it accepts "5%" as well as a flat $ amount.
                      MoneyInput strips non-digits (digits + "." only), so it silently
                      dropped the "%" — making percentage not-in-motion deductibles
                      impossible and inconsistent with the in-motion field. */}
                  <Input value={notMotionDed} onChange={(e) => setNotMotionDed(e.target.value)} placeholder="5% or 500" />
                  <Helper>{deductiblePreview(notMotionDed) || '5% of hull, or flat $ amount (e.g. 500).'}</Helper>
                </Field>
                <Field label="In-motion deductible">
                  <Input value={inMotionDed} onChange={(e) => setInMotionDed(e.target.value)} placeholder="5% or 500" />
                  <Helper>{deductiblePreview(inMotionDed) || '5% of hull, or flat $ amount (e.g. 500).'}</Helper>
                </Field>
              </div>
              {(() => {
                const im = parseDeductible(inMotionDed);
                const nim = parseDeductible(notMotionDed);
                if (!im || !nim || im.kind !== nim.kind || nim.value <= im.value) return null;
                return (
                  <div className="flex items-center gap-2 rounded-card border border-warning/40 bg-warning-soft p-2 text-xs text-text">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-warning" />
                    <span className="flex-1">Not-in-motion is higher — usually reversed.</span>
                    <button
                      type="button"
                      onClick={() => { const a = inMotionDed; setInMotionDed(notMotionDed); setNotMotionDed(a); }}
                      className="rounded-card border border-warning/60 px-2 py-0.5 text-[11px] font-semibold text-warning hover:bg-warning/10"
                    >
                      Swap
                    </button>
                  </div>
                );
              })()}

              {/* NPO toggle relocated to the Pilot Warranty Detail section —
                  it's the switch that determines whether an OPW applies, so it
                  belongs with the warranty, not in Coverage. */}

              {/* Coverage enhancements — CLIENT-FACING: renders on the tower
                  quote card and copies to the Deal at bind. Broker notes below
                  stay internal. */}
              <Field label="Coverage enhancements (shown to client)">
                <textarea
                  rows={2}
                  value={coverageEnhancements}
                  onChange={(e) => setCoverageEnhancements(e.target.value)}
                  placeholder="e.g. Includes premises liability, spare parts coverage, unearned hull premium"
                  maxLength={2000}
                  className={cn(inputClass, 'resize-none')}
                />
              </Field>

              {/* Broker notes */}
              <Field label="Broker notes">
                <textarea
                  rows={2}
                  value={baseNotes}
                  onChange={(e) => setBaseNotes(e.target.value)}
                  placeholder="e.g. quoted from email — full schedule to come"
                  maxLength={2000}
                  className={cn(inputClass, 'resize-none')}
                />
              </Field>
            </div>
          </Section>

          {/* ── PILOT WARRANTY (full OPW vs NPO Approved Pilots) ─── */}
          <Section title="Pilot warranty detail" open={openSections.warranty} onToggle={() => toggle('warranty')}>
            {/* Named Pilots Only is the switch that decides whether an OPW
                applies: NPO and the open pilot warranty are mutually exclusive
                expressions of the same warranty, so the toggle is this section's
                first control. When on, OPW fields disable + clear on save and the
                server applyNpoGuard() still enforces it. */}
            <label className="mb-3 flex items-center gap-2 rounded-card border border-border bg-bg/30 px-3 py-2">
              <input
                type="checkbox"
                checked={npo}
                onChange={(e) => setNpo(e.target.checked)}
                className="h-4 w-4 rounded border-border bg-bg accent-accent"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-text">Named Pilots Only</div>
                <div className="text-[10px] text-text-dim">
                  When on, only the listed pilots are covered — the open pilot warranty below doesn't apply and its fields clear on save (server enforces).
                </div>
              </div>
            </label>
            {/* B14: David's "I&R, OPW only" box, the one writer of
                quotes.ir_opw_only (mapBodyToQuote); it exempts the named-pilot
                requirement and nothing is guessed from aircraft use. Offered
                when LOGGING a quote only: the quote detail an edit loads does
                not carry the stored answer, and a box that cannot show what is
                stored would be a placeholder spelled like an answer. Untouched,
                it sends nothing. */}
            {!savedQuoteId ? (
              <label className="mb-3 flex items-center gap-2 rounded-card border border-border bg-bg/30 px-3 py-2">
                <input type="checkbox" checked={irOpwOnly === true} onChange={(e) => setIrOpwOnly(e.target.checked)}
                  data-ir-opw-only className="h-4 w-4 rounded border-border bg-bg accent-accent" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text">I&amp;R, OPW only</div>
                  <div className="text-[10px] text-text-dim">An instruction and rental risk covered by its open pilot warranty, with no named pilot required.</div>
                </div>
              </label>
            ) : null}
            {npo ? (
              <div className="rounded-card border border-border bg-bg/30 p-3 text-xs text-text-muted">
                Approved Pilots are sourced from the deal's <code>Approved_Pilots</code> subform.
                Confirm or edit which pilots are named on the binding step in old TRACON — full Approved Pilots picker integration lands in V1.5.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="OPW Certificate">
                    <select value={opwCert} onChange={(e) => setOpwCert(e.target.value)} className={inputClass}>
                      <option value="">—</option>
                      {OPW_CERTS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="OPW Ratings">
                    <div className="flex flex-wrap gap-1.5">
                      {OPW_RATINGS.map((r) => {
                        const on = opwRatings.includes(r);
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => toggleRating(r)}
                            className={cn(
                              'rounded-pill border px-2.5 py-1 text-[11px] font-medium',
                              on
                                ? 'border-accent bg-accent-soft text-accent'
                                : 'border-border bg-bg text-text-muted hover:border-border-strong'
                            )}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="OPW Total Hours">
                    <Input value={opwTotalHours} onChange={(e) => setOpwTotalHours(e.target.value)} placeholder="500" inputMode="decimal" />
                  </Field>
                  <Field label="OPW Type Hours (free text)">
                    <Input value={opwTypeHours} onChange={(e) => setOpwTypeHours(e.target.value)} placeholder='e.g. "50 hrs retract"' />
                  </Field>
                  <Field label="OPW Make/Model Hours">
                    <Input value={opwMakeModelHours} onChange={(e) => setOpwMakeModelHours(e.target.value)} placeholder="25" inputMode="decimal" />
                  </Field>
                </div>
                <Field label="OPW Full Text (raw clause)">
                  <textarea
                    rows={3}
                    value={opwFullText}
                    onChange={(e) => setOpwFullText(e.target.value)}
                    placeholder="Paste the carrier's full OPW clause if it will not break into the boxes above"
                    className={cn(inputClass, 'resize-none')}
                  />
                </Field>
                {opwWarning ? (
                  <div className="mt-2 flex items-start gap-2 rounded-card border border-warning/40 bg-warning/10 p-2.5 text-[11px] leading-relaxed text-warning">
                    <AlertTriangle size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                    <span>{opwWarning}</span>
                  </div>
                ) : null}
              </>
            )}
          </Section>

          {/* ── NAMED PILOTS (carrier decision + requirement, merged) ──
              One surface per pilot. The two underlying data fields stay distinct
              — pilot_approval_states (the carrier's decision) and the requirement
              text (pilot_requirements / approved_pilot_requirements) — they're
              just rendered together so the broker isn't choosing between two
              redundant-looking menus. A pilot can be approved AND carry a
              requirement; decision ≠ requirement. Presentation merge only —
              every state binding below is unchanged, so the saved payload is
              byte-equivalent to the old two-section flow. */}
          <Section title="Named Pilots" open={openSections.pilotReqs} onToggle={() => toggle('pilotReqs')}>
            <p className="text-xs text-text-muted">
              Each named pilot's <strong>carrier decision</strong> (approved / conditional / declined —
              recorded per carrier; a submitted PHF is not an approval) and any
              <strong> requirement</strong> the underwriter imposed (training, hours, endorsements).
              The two are independent — a pilot can be approved <em>and</em> carry a requirement.
            </p>

            {scenarioScope ? (
              <div className="mt-3 space-y-4">
                {/* Carrier decision per rated pilot */}
                <div className="space-y-2">
                  <div className="text-xs font-medium text-text-muted">Carrier decision</div>
                  {approvalRows.length === 0 ? (
                    <p className="text-[11px] text-text-dim">No rated pilots on this submission yet.</p>
                  ) : approvalRows.map((p) => {
                    const cur = approvalByPilot[String(p.contact_id)] || {};
                    // Conditional is not a choice, it is what a condition MAKES
                    // an approval. The requirement lives in the pilot x tail
                    // matrix below, which is this path's only place to type it.
                    const decision = cur.status === 'conditional' ? 'approved' : (cur.status || '');
                    return (
                      <div key={p.contact_id} className="space-y-2 rounded-card border border-border bg-bg/30 p-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="min-w-[120px] flex-1 text-sm font-medium text-text">{p.name || '—'}</span>
                          <select
                            value={decision}
                            onChange={(e) => setApproval(p.contact_id, p.name, { status: e.target.value, touched: true })}
                            className={cn(inputClass, 'w-44')}
                            aria-label={`Carrier decision for ${p.name || 'pilot'}`}
                          >
                            <option value="">— No decision —</option>
                            <option value="approved">Approved</option>
                            <option value="declined">Declined</option>
                            <option value="excluded">Excluded</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Requirements — the canonical pilot×tail APR matrix */}
                <div className="space-y-3">
                  <div className="text-xs font-medium text-text-muted">Requirements (per pilot · tail)</div>
                  <p className="text-[11px] text-text-dim">
                    Additional Pilot Requirements — the training the underwriter imposed, scoped to a pilot on a tail
                    (e.g. "10 hrs dual + 5 full-stop landings"). Add one row per requirement.
                  </p>
                  {aprEntries.length > 0 && (
                    <div className="space-y-2">
                      {aprEntries.map((e, idx) => (
                        <div key={idx} className="space-y-2 rounded-card border border-border bg-bg/30 p-2.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={e.contact_id}
                              onChange={(ev) => setAprPilot(idx, ev.target.value)}
                              className={cn(inputClass, 'min-w-[140px] flex-1')}
                            >
                              {!ratedPilots.some((rp) => String(rp.contact_id) === String(e.contact_id)) && (
                                <option value={e.contact_id}>{e.name || 'Select pilot'}</option>
                              )}
                              {ratedPilots.map((rp) => <option key={rp.contact_id} value={rp.contact_id}>{rp.name}</option>)}
                            </select>
                            {tailOptions.length > 1 ? (
                              <select
                                value={e.tail_number}
                                onChange={(ev) => updateAprEntry(idx, { tail_number: ev.target.value })}
                                className={cn(inputClass, 'w-32 font-mono')}
                              >
                                {tailOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                            ) : (
                              <span className="rounded-pill border border-border bg-bg px-2.5 py-1 font-mono text-[11px] text-text-muted">
                                {e.tail_number || singleTail || '—'}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeAprEntry(idx)}
                              className="ml-auto text-text-dim hover:text-accent"
                              aria-label="Remove requirement"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <textarea
                            rows={2}
                            value={e.requirement}
                            onChange={(ev) => updateAprEntry(idx, { requirement: ev.target.value })}
                            placeholder="e.g. 10 hrs dual instruction + 5 full-stop landings to a full stop"
                            className={cn(inputClass, 'resize-none')}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addAprEntry}
                    disabled={!ratedPilots.length || !tailOptions.length}
                    className="inline-flex items-center gap-1 rounded-pill border border-border bg-bg px-3 py-1.5 text-xs font-medium text-text hover:border-border-strong disabled:opacity-50"
                  >
                    <Plus size={14} /> Add requirement
                  </button>
                  {(!ratedPilots.length || !tailOptions.length) && (
                    <p className="text-[11px] text-text-dim">
                      Roster and aircraft come from the submission — add rated pilots and included aircraft there to scope APRs.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {approvalRows.length === 0 ? (
                  <p className="text-[11px] text-text-dim">
                    No pilots on this deal yet — add them in the binding step to record decisions and requirements.
                  </p>
                ) : (
                  approvalRows.map((p) => {
                    const cur = approvalByPilot[String(p.contact_id)] || {};
                    // Conditional is not a choice, it is what a condition MAKES
                    // an approval, so a stored 'conditional' shows as Approved
                    // with its text underneath. Same model as the wizard.
                    const decision = cur.status === 'conditional' ? 'approved' : (cur.status || '');
                    // ONE box, always shown. The `canRequire` gate that used to
                    // wrap this read from the Approved_Pilots-derived prefill
                    // list, so a roster pilot the subform did not know about saw
                    // ONLY the decoy condition input and had nowhere to type a
                    // requirement that prints.
                    const reqText = (pilotApr[p.name] || '').trim();
                    const derivedStatus = encodeApprovalStatus({ ...cur, condition_text: reqText });
                    const bareConditional = derivedStatus === 'conditional' && !reqText;
                    return (
                      <div key={p.contact_id} className="space-y-2 rounded-card border border-border bg-bg/30 p-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="min-w-[120px] flex-1 text-sm font-medium text-text">{p.name || '—'}</span>
                          <select
                            value={decision}
                            onChange={(e) => setApproval(p.contact_id, p.name, { status: e.target.value, touched: true })}
                            className={cn(inputClass, 'w-44')}
                            aria-label={`Carrier decision for ${p.name || 'pilot'}`}
                          >
                            <option value="">— No decision —</option>
                            <option value="approved">Approved</option>
                            <option value="declined">Declined</option>
                            <option value="excluded">Excluded</option>
                          </select>
                        </div>
                        {bareConditional ? (
                          <p className="text-[11px] text-text-muted">
                            Recorded conditional by the carrier with no condition stated. Type the requirement below, or pick a decision to change it.
                          </p>
                        ) : null}
                        <AutoTextarea
                          value={pilotApr[p.name] || ''}
                          onChange={(v) => setPilotApr((prev) => ({ ...prev, [p.name]: v }))}
                          placeholder={decision === 'excluded'
                            ? `The carrier's exclusion wording for ${p.name}, e.g. may not operate the aircraft controls at any time`
                            : decision === 'declined'
                            ? `Why ${p.name} was declined, and anything the carrier still requires`
                            : `Anything the carrier requires of ${p.name}, e.g. 25 hrs dual before solo`}
                        />
                        {reqText ? (
                          <p className="text-[11px] text-text-muted">Approved with conditions. This prints on {p.name}&rsquo;s application.</p>
                        ) : null}
                      </div>
                    );
                  })
                )}

                {/* UNOFFERABLE. Roster pilots with no CRM Contact carry no
                    decision and no requirement, because there is no record to
                    carry them against, but they are NOT hidden: this list used
                    to be shorter than the roster with no explanation, which on
                    this field means a pilot nobody has noticed is uninsured. */}
                {unofferablePilots.map((p) => (
                  <div key={p.roster_key} className="space-y-1 rounded-card border border-warning/40 bg-warning-soft p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-[120px] flex-1 text-sm font-medium text-text">{p.name || 'Unnamed pilot'}</span>
                      <span className="rounded-pill border border-warning/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning">
                        Cannot be offered
                      </span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-text">
                      {p.blocked_reason === 'no_crm_contact'
                        ? `${p.name || 'This pilot'} is on the roster with no CRM contact, so the carrier's decision cannot be recorded and they cannot be named on the policy. Confirm them in the roster to create the contact.`
                        : `${p.name || 'This pilot'} cannot be named on the policy (${p.blocked_reason || 'reason not stated'}).`}
                    </p>
                  </div>
                ))}

                {/* Requirements that apply to EVERY pilot, each carrying the
                    aircraft it belongs to. A tail-scoped one prints once under
                    that aircraft; an All-aircraft one prints once after the
                    last. Neither ever repeats per pilot. The "move to" chips
                    below relocate a row into a specific pilot's block above. */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-text-muted">Requirements for every pilot</span>
                    <button
                      type="button"
                      onClick={() => setCommonReqs([...commonReqs, { tail_number: '', requirement: '' }])}
                      className="inline-flex items-center gap-1 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  {commonReqs.length === 0 ? (
                    <p className="text-[11px] text-text-dim">Nothing that applies to every pilot on this quote.</p>
                  ) : commonReqs.map((row, i) => (
                    <div key={i} className="space-y-1.5 rounded-card border border-border bg-bg/30 p-2.5">
                      <div className="flex items-center gap-2">
                        <select
                          value={row.tail_number || ''}
                          onChange={(e) => setCommonReq(i, { tail_number: e.target.value })}
                          className={cn(inputClass, 'w-44')}
                          aria-label="Aircraft this requirement applies to"
                        >
                          <option value="">All aircraft</option>
                          {tailOptions.map((t) => <option key={t} value={t}>{t} only</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => setCommonReqs(commonReqs.filter((_, j) => j !== i))}
                          className="ml-auto text-text-dim hover:text-accent"
                          aria-label="Remove requirement"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <AutoTextarea
                        value={row.requirement || ''}
                        onChange={(v) => setCommonReq(i, { requirement: v })}
                        placeholder={row.tail_number
                          ? `What every pilot of ${row.tail_number} must do, e.g. annual recurrent training in make and model`
                          : 'What every pilot on the policy must do, whatever they fly'}
                      />
                      {(row.requirement || '').trim() ? (
                        <p className="text-[11px] text-text-muted">
                          {row.tail_number
                            ? `Prints once under ${row.tail_number}, not on the other aircraft.`
                            : 'Prints once at the end of the pilot list, on every aircraft.'}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
                {commonText.trim() && approvalPilots.length >= 2 && (
                  <div className="flex items-start gap-2 rounded-card border border-warning/40 bg-warning-soft p-2 text-xs text-text">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
                    <div>
                      This prints on <strong>all {approvalPilots.length}</strong> pilots' applications.
                      {suggestedAprPilots.length > 0 && (
                        <> Looks specific to <strong>{suggestedAprPilots.map((p) => p.name).join(', ')}</strong> — move it instead?</>
                      )}
                    </div>
                  </div>
                )}
                {commonText.trim() && approvalPilots.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-medium text-text-muted">Move to:</span>
                    {approvalPilots.map((p) => {
                      const suggested = suggestedAprSet.has(p.contact_id);
                      return (
                        <button
                          key={p.contact_id}
                          type="button"
                          onClick={() => assignCommonToPilot(p.name)}
                          className={cn(
                            'rounded-pill border px-2.5 py-1 text-[11px] font-medium',
                            suggested
                              ? 'border-accent bg-accent-soft text-accent'
                              : 'border-border bg-bg text-text hover:border-border-strong'
                          )}
                        >
                          {p.name}{suggested ? ' •' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ── PREMIUM BREAKDOWN (optional) ──────────────────────── */}
          {/* Estimated taxes was pulled up next to Premium (the only field the
              broker routinely touches). Hull/Liability/TRIA stay here for the
              rare carrier that itemizes the split; the Δ-vs-total validator is
              unchanged (it never read taxes). */}
          <Section title="Premium breakdown (optional)" open={openSections.breakdown} onToggle={() => toggle('breakdown')}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Hull premium">
                <Input value={hullPremium} onChange={(e) => setHullPremium(e.target.value)} placeholder="hull portion $" inputMode="decimal" />
              </Field>
              <Field label="Liability premium">
                <Input value={liabilityPremium} onChange={(e) => setLiabilityPremium(e.target.value)} placeholder="liability portion $" inputMode="decimal" />
              </Field>
              <Field label="TRIA premium">
                <Input value={triaPremium} onChange={(e) => setTriaPremium(e.target.value)} placeholder="TRIA portion $" inputMode="decimal" />
              </Field>
            </div>
            {premiumValidation ? (
              <div className={cn(
                'mt-2 flex items-center gap-2 rounded-card border px-3 py-2 text-[11px]',
                premiumValidation.kind === 'warn'
                  ? 'border-warning/50 bg-warning-soft text-warning'
                  : 'border-success/30 bg-success-soft text-success'
              )}>
                {premiumValidation.kind === 'warn' ? <AlertTriangle size={14} /> : null}
                <span>{premiumValidation.message}</span>
              </div>
            ) : null}
          </Section>

          {/* ── COVERAGE PARTS subform ────────────────────────────── */}
          <Section title="Coverage parts" open={openSections.parts} onToggle={() => toggle('parts')}>
            <div className="space-y-2">
              {coverageParts.length === 0 ? (
                <p className="text-[12px] text-text-dim">No coverage parts. Most personal-aircraft quotes leave this empty.</p>
              ) : (
                coverageParts.map((r, idx) => {
                  const isPct = r.price_mode === 'liability_premium' || r.price_mode === 'hull_premium';
                  return (
                  <div key={r.id || idx} className="space-y-2 rounded-card border border-border bg-bg/30 p-3">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_2fr_auto]">
                      <Field label="Part type">
                        <select value={r.part_type} onChange={(e) => updateCoveragePart(idx, { part_type: e.target.value })} className={inputClass}>
                          {PART_TYPE_PICKLIST.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </Field>
                      <Field label="Limit">
                        <Input value={r.limit} onChange={(e) => updateCoveragePart(idx, { limit: e.target.value })} inputMode="decimal" />
                      </Field>
                      <Field label="Sub-limit">
                        <Input value={r.sub_limit} onChange={(e) => updateCoveragePart(idx, { sub_limit: e.target.value })} inputMode="decimal" />
                      </Field>
                      <Field label="Notes">
                        <Input value={r.broker_notes} onChange={(e) => updateCoveragePart(idx, { broker_notes: e.target.value })} placeholder="(per-part notes)" />
                      </Field>
                      <div className="flex items-end justify-end">
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCoveragePart(idx)} aria-label="Remove">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    {/* Part A: optional election + per-option pricing. Optional parts
                        surface as client-electable checkboxes on the quote card;
                        percentage-of pricing (e.g. TRIA = 10% of liability premium)
                        prices per option so a shared part is right on every option. */}
                    <div className="flex flex-wrap items-end gap-3">
                      <label className="flex items-center gap-1.5 pb-1.5 text-[12px] text-text-muted">
                        <input type="checkbox" checked={r.optional === true} onChange={(e) => updateCoveragePart(idx, { optional: e.target.checked })} />
                        Optional (client can elect)
                      </label>
                      <Field label="Pricing">
                        <select value={r.price_mode || 'flat'} onChange={(e) => updateCoveragePart(idx, { price_mode: e.target.value })} className={inputClass}>
                          <option value="flat">Flat premium</option>
                          <option value="liability_premium">% of liability premium</option>
                          <option value="hull_premium">% of hull premium</option>
                        </select>
                      </Field>
                      {isPct ? (
                        <Field label="Percent">
                          <Input value={r.pct} onChange={(e) => updateCoveragePart(idx, { pct: e.target.value })} inputMode="decimal" placeholder="10" />
                        </Field>
                      ) : (
                        <Field label="Premium">
                          <Input value={r.premium} onChange={(e) => updateCoveragePart(idx, { premium: e.target.value })} inputMode="decimal" />
                        </Field>
                      )}
                      {r.optional && (
                        <label className="flex items-center gap-1.5 pb-1.5 text-[12px] text-text-muted">
                          <input type="checkbox" checked={r.elected_default === true} onChange={(e) => updateCoveragePart(idx, { elected_default: e.target.checked })} />
                          Checked by default
                        </label>
                      )}
                    </div>
                    {r.optional && (
                      <Field label="Client blurb (optional, shown in the info popover)">
                        <Input value={r.client_blurb} onChange={(e) => updateCoveragePart(idx, { client_blurb: e.target.value })} placeholder="Plain-language description for the client (falls back to a built-in default, then broker notes)" />
                      </Field>
                    )}
                  </div>
                  );
                })
              )}
              <Button type="button" variant="outline" size="sm" onClick={addCoveragePart} className="gap-1.5">
                <Plus size={14} /> Add part
              </Button>
            </div>
          </Section>

          {/* ── SUBJECTIVITIES ────────────────────────────────────── */}
          <Section title="Subjectivities" open={openSections.subjectivities} onToggle={() => toggle('subjectivities')}>
            <div className="space-y-2">
              <textarea
                rows={4}
                value={subjectivities}
                onChange={(e) => setSubjectivities(e.target.value)}
                placeholder="One per line. Broker-internal, never shown to the client."
                className={cn(inputClass, 'resize-none')}
              />
              <Helper>Phase 1: stored inside broker_notes as a [Subj] block; Phase 2 promotes to a real field.</Helper>
            </div>
          </Section>

          {/* ── SOURCE DOCUMENT (Phase-2 placeholder) ──────────────── */}
          <Section title="Source document (PDF)" open={openSections.source} onToggle={() => toggle('source')}>
            <div className="rounded-card border border-dashed border-border bg-bg/30 p-4 text-center">
              <p className="text-sm text-text-muted">
                Quote PDF upload — Phase 2 (backend route in progress).
              </p>
              <p className="mt-2 text-[11px] text-text-dim">
                For now, place the carrier's quote PDF directly in the deal's WorkDrive folder under
                <br />
                <code>Applications/{new Date().getFullYear()}/Accounts/&lt;account&gt;/&lt;app-#&gt;/quotes/</code>
              </p>
            </div>
          </Section>

          {/* ── VISIBILITY & RECOMMENDATION ───────────────────────── */}
          {/* Renamed from "Internal flags": client_visible is inherently
              EXTERNAL (it controls what the client sees on tower.html), so
              "internal" was a misnomer. Field keys unchanged. */}
          <Section title="Visibility & recommendation" open={openSections.internal} onToggle={() => toggle('internal')}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="flex items-center gap-2 rounded-card border border-border bg-bg/30 px-3 py-2">
                <input
                  type="checkbox"
                  checked={clientVisible}
                  onChange={(e) => setClientVisible(e.target.checked)}
                  className="h-4 w-4 rounded border-border bg-bg accent-accent"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text">Client visible</div>
                  <div className="text-[10px] text-text-dim">Surfaces on tower.html for the client.</div>
                </div>
              </label>
              <label className="flex items-center gap-2 rounded-card border border-border bg-bg/30 px-3 py-2">
                <input
                  type="checkbox"
                  checked={brokerRecommended}
                  onChange={(e) => setBrokerRecommended(e.target.checked)}
                  className="h-4 w-4 rounded border-border bg-bg accent-accent"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text">Broker recommended</div>
                  <div className="text-[10px] text-text-dim">Your pick of the set.</div>
                </div>
              </label>
            </div>
          </Section>

          {/* Per-aircraft schedule (advanced — collapsed by default).
              Broker can refine per-row use / deductibles / training /
              per-aircraft premium even when the top Hull is enough. */}
          {aircraft.length > 0 ? (
            <Section
              title="Per-aircraft schedule (advanced)"
              open={openSections.schedule}
              onToggle={() => toggle('schedule')}
            >
              <PerAircraftSchedule
                schedule={schedule}
                updateScheduleRow={updateScheduleRow}
                headerInMotionDed={inMotionDed}
                headerNotMotionDed={notMotionDed}
                headerCoverageType={coverageType}
              />
            </Section>
          ) : null}

          {/* ── QUOTE REFERENCE (UW + ref + dates + term + territory) ─ */}
          <Section title="Quote reference" open={openSections.identity} onToggle={() => toggle('identity')}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Underwriter (contact)">
                <Input value={underwriter} onChange={(e) => setUnderwriter(e.target.value)} placeholder="UW name at the carrier" />
              </Field>
              <Field label="Carrier quote reference">
                <Input value={carrierRef} onChange={(e) => setCarrierRef(e.target.value)} placeholder="(carrier's quote number)" />
              </Field>
              <Field label="Quote date">
                <Input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} />
              </Field>
              {/* "Quote expiration" was here and is DELETED (David 2026-07-29):
                  it duplicated the "Offer expires" picker in the headline block,
                  which already writes quotes.quote_expires_at. Two inputs for
                  one date is how they diverge, and conflating dates on this
                  table corrupted 10 binds once already. */}
              {/* The shared picker (decision 14): 12 pre-picked on a new log,
                  6 and Other beside it, sent as shown. The select it replaces
                  sent 'Other' as no term at all. */}
              <Field label="Term length (months)">
                <div><TermPicker value={termMonths} onChange={setTermMonths} label="" /></div>
              </Field>
              <Field label="Territory override">
                <Input value={territory} onChange={(e) => setTerritory(e.target.value)} placeholder="(default US)" />
              </Field>
            </div>
            <Helper>
              Phase 1: stored in broker_notes as [Ref: …] / [QDate: …] / etc.; Phase 2 promotes to real fields.
            </Helper>
          </Section>
        </div>

        <SheetFooter className="border-t border-border bg-surface px-1 py-2">
          {/* A silently-disabled Save reads as a dead button — say why. The
              effective-date gate especially: an edit-opened quote can arrive
              with no proposed_effective and nothing else looks wrong. */}
          {!canSave && !submitting && missingRequired.length > 0 ? (
            <span className="self-center text-[11px] text-warning sm:mr-auto">
              {missingRequired.length === 1
                ? `Enter ${missingRequired[0]} to save`
                : `To save, enter: ${missingRequired.join(' · ')}`}
            </span>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          {scenarioScope ? (
            /* Cell-scoped panel stays lean: presenting/visibility is a
               deal-level workflow, not a matrix-cell concern (4b-2 decision).
               client_visible remains togglable in Internal flags. */
            <Button type="button" disabled={!canSave} onClick={() => doSave()}>
              {submitting ? 'Saving…' : 'Save quote'}
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={!canSave} onClick={() => doSave({ markVisible: true })}>
                Save & mark visible
              </Button>
              <Button type="button" disabled={!canSave} onClick={() => doSave({ present: true })}>
                {submitting ? 'Saving…' : 'Save & present'}
              </Button>
              <Button type="button" variant="outline" disabled={!canSave} onClick={() => doSave()}>
                Save
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Reusable section wrapper ────────────────────────────────────────
function Section({ title, open, onToggle, primary, children }) {
  return (
    <section className={cn(
      'rounded-card border',
      open ? 'border-border-strong bg-surface-strong' : 'border-border bg-surface'
    )}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between border-b border-border px-4 py-2.5 text-left"
      >
        <h3 className={cn(
          'text-xs font-semibold uppercase tracking-[0.14em]',
          primary ? 'text-text' : 'text-text-muted'
        )}>
          {title}
        </h3>
        {open
          ? <ChevronDown size={16} strokeWidth={1.5} className="text-text-dim" />
          : <ChevronRight size={16} strokeWidth={1.5} className="text-text-dim" />}
      </button>
      {open ? <div className="p-4">{children}</div> : null}
    </section>
  );
}

function PerAircraftSchedule({ schedule, updateScheduleRow, headerInMotionDed, headerNotMotionDed, headerCoverageType }) {
  if (!schedule || schedule.length === 0) return null;
  return (
    <div className="space-y-2">
      {schedule.map((r, idx) => (
        <div key={r.tail_number_id || idx} className="rounded-card border border-border bg-bg/30 p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-text">{r.tail_display || '(no tail)'}</div>
              <div className="truncate text-[10px] text-text-dim">{r.year} {r.make_model}</div>
            </div>
            {r.id ? (
              <span className="rounded-pill bg-info-soft px-2 py-0.5 text-[9px] uppercase tracking-wide text-info">
                row-id preserved
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {/* Per-aircraft coverage override only when the policy actually has
                2+ aircraft (a fleet can mix Full Flight on one, Ground Only on a
                project plane). With a single aircraft the policy-level Coverage
                type IS the aircraft term — set once in the header, no per-row
                control. Free text → Aircraft_on_Quote.Coverage_Type → at bind
                Aircraft_on_Policy.Coverage_Type. */}
            {schedule.length >= 2 ? (
              <Field label="Coverage type (override)">
                <select
                  value={r.coverage_type || ''}
                  onChange={(e) => updateScheduleRow(idx, { coverage_type: e.target.value })}
                  className={inputClass}
                >
                  {/* NO FULL FLIGHT DEFAULT (S6, decision 12): blank inherits
                      the policy coverage type, and a policy type nobody stated
                      stays unstated, so the present gate lists it. */}
                  <option value="">{headerCoverageType ? `inherit (${headerCoverageType})` : 'not stated'}</option>
                  <ListOptions field="coverage_type" stored={r.coverage_type} />
                </select>
              </Field>
            ) : null}
            <Field label="Aircraft use">
              <select value={r.aircraft_use || ''} onChange={(e) => updateScheduleRow(idx, { aircraft_use: e.target.value })} className={inputClass}>
                <option value="">not stated</option>
                <ListOptions field="aircraft_use" stored={r.aircraft_use} />
              </select>
            </Field>
            <Field label="Aircraft premium">
              <Input value={r.aircraft_premium} onChange={(e) => updateScheduleRow(idx, { aircraft_premium: e.target.value })} placeholder="per-aircraft $" inputMode="decimal" />
            </Field>
            {/* The training checkbox is GONE (David 2026-08-12): a training
                requirement is the carrier's answer and lives in
                Pilot_Requirements; this control was ticked on 2 of 180
                optioned quotes and wrote to a store nothing reads. Stored
                values pass through untouched. */}
            <Field label="Per-row not-in-motion ded">
              <Input value={r.deductible_not_in_motion} onChange={(e) => updateScheduleRow(idx, { deductible_not_in_motion: e.target.value })} placeholder={headerNotMotionDed ? `blank = header (${headerNotMotionDed})` : '500'} />
            </Field>
            <Field label="Per-row in-motion ded">
              <Input value={r.deductible_in_motion} onChange={(e) => updateScheduleRow(idx, { deductible_in_motion: e.target.value })} placeholder={headerInMotionDed ? `blank = header (${headerInMotionDed})` : '5% or 500'} />
            </Field>
          </div>
        </div>
      ))}
    </div>
  );
}

// MoneyInput / Field / Helper moved to @/components/quote/QuoteControls.jsx,
// unchanged, so the wizard renders the same controls (QUOTE-WIZARD-SPEC phase 1).

// Match the V2 mobile breakpoint (Tailwind md = 768px). Hook updates
// on resize so the sheet's default-open state recomputes when the
// viewport crosses the boundary.
function useMobileViewport() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    function onChange(e) { setIsMobile(e.matches); }
    mq.addEventListener('change', onChange);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}
